import { memo, useEffect, useState } from 'react'

interface LiveDateTimeProps {
  /** Custom className for styling */
  className?: string
}

/**
 * Displays the current date and time, updating every minute.
 * This component manages its own state to prevent parent re-renders.
 */
const LiveDateTime: React.FC<LiveDateTimeProps> = memo(({ className }) => {
  const [currentTime, setCurrentTime] = useState(() => new Date())

  useEffect(() => {
    // Update immediately to sync with the current minute
    const now = new Date()
    setCurrentTime(now)

    // Calculate milliseconds until the next minute starts
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds()

    // Set initial timeout to sync with minute boundary
    const initialTimeout = setTimeout(() => {
      setCurrentTime(new Date())

      // Then set interval to update every minute
      const interval = setInterval(() => {
        setCurrentTime(new Date())
      }, 60000)

      // Store interval ID for cleanup
      ;(
        window as unknown as { __liveDateTimeInterval?: NodeJS.Timeout }
      ).__liveDateTimeInterval = interval
    }, msUntilNextMinute)

    return () => {
      clearTimeout(initialTimeout)
      const storedInterval = (
        window as unknown as { __liveDateTimeInterval?: NodeJS.Timeout }
      ).__liveDateTimeInterval
      if (storedInterval) {
        clearInterval(storedInterval)
      }
    }
  }, [])

  const formatDateTime = (date: Date) => {
    // Format: "January 23, 2026, 21:28 ET"
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    // Get timezone abbreviation (e.g., ET, CT, MT, PT, etc.)
    const tzAbbr = date
      .toLocaleTimeString('en-US', { timeZoneName: 'short' })
      .split(' ')
      .pop()

    return `${dateStr}, ${timeStr} ${tzAbbr}`
  }

  return <span className={className}>{formatDateTime(currentTime)}</span>
})

LiveDateTime.displayName = 'LiveDateTime'

export default LiveDateTime
